/**
 * PricingPage - Public pricing page for EQ Scout
 *
 * Accessible without authentication at /pricing.
 * Follows the EQ Pricing Page Spec:
 * - 3-column subscription grid (Starter, Growth, Scale)
 * - Credit top-up section below with volume pricing
 * - "What is a Credit?" FAQ
 * - No $0 free tier card — freemium messaging in header
 *
 * Brand: EQ.app — #5a20ff (purple), #52dcb3 (teal), #f8f8f6 (off-white)
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap,
  Users,
  Crown,
  Check,
  ArrowRight,
  Search,
  Mail,
  MessageSquare,
  Database,
  HelpCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const PLANS = [
  {
    key: "starter",
    name: "Starter",
    price: 49,
    headline: "The Sales Navigator Killer.",
    target: "Solo operators & casual business development.",
    color: "#5a20ff",
    icon: Zap,
    features: [
      "40 Credits per month",
      "Full access to EQscout AI Search",
      "Basic AI Outreach generation",
      "CRM integrations",
    ],
  },
  {
    key: "growth",
    name: "Growth",
    price: 149,
    headline: "The Core BD Engine.",
    target: "Active recruiters & dedicated B2B sales reps.",
    color: "#52dcb3",
    icon: Users,
    popular: true,
    features: [
      "200 Credits per month",
      "Everything in Starter",
      "Advanced AI Outreach sequencing",
      "Priority email support",
    ],
  },
  {
    key: "scale",
    name: "Scale",
    price: 499,
    headline: "AI Transformation at Scale.",
    target: "Agency owners & high-volume teams.",
    color: "#3b82f6",
    icon: Crown,
    features: [
      "750 Credits per month",
      "Everything in Growth",
      "EQenrich bulk database uploads",
      "Multi-user shared team credit pools",
      "API Access",
    ],
  },
];

const CREDIT_TIERS = [
  { range: "25 – 99", price: "$1.50" },
  { range: "100 – 499", price: "$1.25" },
  { range: "500 – 999", price: "$1.00" },
  { range: "1,000 – 2,499", price: "$0.80" },
  { range: "2,500+", price: "$0.65" },
];

const FAQ_ITEMS = [
  {
    q: "Do subscription credits roll over?",
    a: "Subscription credits reset at the beginning of each billing cycle to keep things clean.",
  },
  {
    q: "Do Top-Up credits roll over?",
    a: "Yes, purchased Top-Up credits remain in your account for 12 months.",
  },
  {
    q: "Can I search without credits?",
    a: "Yes! Searching the EQ database to find your ideal targets is completely free. You only spend credits when you want to reveal their contact info or engage them.",
  },
];

function PlanCard({ plan, onGetStarted }) {
  const Icon = plan.icon;
  return (
    <div className="relative flex flex-col rounded-3xl bg-white p-6 md:p-8 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 border border-black/5">
      {plan.popular && (
        <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] px-5 py-1.5 text-xs font-bold text-white uppercase tracking-wider shadow-lg">
          Most Popular
        </span>
      )}

      <div className="mb-5">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 shadow-lg"
          style={{ backgroundColor: `${plan.color}15`, boxShadow: `0 8px 24px ${plan.color}20` }}
        >
          <Icon className="w-6 h-6" style={{ color: plan.color }} />
        </div>
        <h3
          className="text-2xl font-bold text-black mb-1"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {plan.name}
        </h3>
        <p className="text-sm font-medium" style={{ color: plan.color }}>
          {plan.headline}
        </p>
        <p className="text-xs text-black/40 mt-1">{plan.target}</p>
      </div>

      <div className="mb-6">
        <span className="text-4xl font-extrabold text-black">${plan.price}</span>
        <span className="text-sm text-black/40 ml-1">/ month</span>
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-black/70">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#52dcb3]" />
            {f}
          </li>
        ))}
      </ul>

      <button
        onClick={() => onGetStarted(plan.key)}
        className="w-full py-3.5 rounded-2xl font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 active:scale-95"
        style={
          plan.popular
            ? {
                background: `linear-gradient(135deg, ${plan.color}, #5a20ff)`,
                color: "white",
                boxShadow: `0 8px 24px ${plan.color}30`,
              }
            : {
                border: `2px solid ${plan.color}30`,
                color: plan.color,
              }
        }
        onMouseEnter={(e) => {
          if (!plan.popular) {
            e.target.style.backgroundColor = `${plan.color}08`;
          }
        }}
        onMouseLeave={(e) => {
          if (!plan.popular) {
            e.target.style.backgroundColor = "transparent";
          }
        }}
      >
        Get Started
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function FAQItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-black/5 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-sm font-semibold text-black group-hover:text-[#5a20ff] transition-colors">
          {item.q}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-black/30 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-black/30 flex-shrink-0" />
        )}
      </button>
      {open && (
        <p className="text-sm text-black/60 pb-5 -mt-1 leading-relaxed">{item.a}</p>
      )}
    </div>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();

  const handleGetStarted = (planKey) => {
    navigate(`/app?upgrade=${planKey}`);
  };

  return (
    <div className="min-h-screen bg-[#f8f8f6]">
      {/* Subtle grain overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-50"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Nav bar */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 max-w-7xl mx-auto">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 group"
        >
          <div className="w-9 h-9 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-xl flex items-center justify-center shadow-lg shadow-[#5a20ff]/20">
            <Search className="w-4.5 h-4.5 text-white" />
          </div>
          <span
            className="text-lg font-bold text-black group-hover:text-[#5a20ff] transition-colors"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            EQ Scout
          </span>
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/app")}
            className="px-5 py-2.5 text-sm font-semibold text-black/60 hover:text-black transition-colors"
          >
            Log in
          </button>
          <button
            onClick={() => navigate("/app")}
            className="px-5 py-2.5 bg-[#5a20ff] text-white text-sm font-bold rounded-full hover:bg-[#4a18d9] transition-all shadow-lg shadow-[#5a20ff]/20 active:scale-95"
          >
            Try Free
          </button>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative z-10 text-center px-6 pt-12 pb-8 md:pt-20 md:pb-12 max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-white border border-black/5 rounded-full px-4 py-2 mb-6 shadow-sm">
          <Search className="w-4 h-4 text-[#5a20ff]" />
          <span className="text-xs font-semibold text-black/60">
            Search for Free, Pay for Reveals
          </span>
        </div>
        <h1
          className="text-4xl md:text-6xl font-bold text-black mb-4 leading-tight"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Simple pricing,{" "}
          <span className="bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] bg-clip-text text-transparent">
            powerful results
          </span>
        </h1>
        <p className="text-lg md:text-xl text-black/50 max-w-2xl mx-auto leading-relaxed">
          Find your ideal contacts for free. Only pay when you're ready to
          connect. No contracts, cancel anytime.
        </p>
      </header>

      {/* Plan Cards */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5">
          {PLANS.map((plan) => (
            <PlanCard key={plan.key} plan={plan} onGetStarted={handleGetStarted} />
          ))}
        </div>

        {/* Enterprise */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-3 bg-white border border-black/5 rounded-2xl px-6 py-4 shadow-sm">
            <Crown className="w-5 h-5 text-yellow-500" />
            <div className="text-left">
              <span className="text-sm font-bold text-black">Need Enterprise?</span>
              <p className="text-xs text-black/40">Custom credits, dedicated support, SLAs</p>
            </div>
            <a
              href="mailto:hello@eq.app?subject=EQ Scout Enterprise Inquiry"
              className="px-4 py-2 bg-black text-white text-xs font-bold rounded-full hover:bg-black/80 transition-colors flex items-center gap-1.5"
            >
              <Mail className="w-3.5 h-3.5" />
              Contact Sales
            </a>
          </div>
        </div>
      </section>

      {/* Credit Top-Ups */}
      <section className="relative z-10 bg-white border-t border-black/5">
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#5a20ff]/5 rounded-full px-4 py-2 mb-4">
              <Zap className="w-4 h-4 text-[#5a20ff]" />
              <span className="text-xs font-bold text-[#5a20ff] uppercase tracking-wider">
                Pay-As-You-Go
              </span>
            </div>
            <h2
              className="text-3xl md:text-4xl font-bold text-black mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Credit Top-Ups
            </h2>
            <p className="text-black/50 max-w-lg mx-auto">
              Need more fuel this month? Buy Top-Up credits instantly. Volume
              discounts apply.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-[#f8f8f6] rounded-3xl overflow-hidden border border-black/5">
              <div className="grid grid-cols-2 px-6 py-3 bg-black/[0.02] border-b border-black/5">
                <span className="text-xs font-bold text-black/40 uppercase tracking-wider">
                  Volume
                </span>
                <span className="text-xs font-bold text-black/40 uppercase tracking-wider text-right">
                  Per Credit
                </span>
              </div>
              {CREDIT_TIERS.map((tier, i) => (
                <div
                  key={tier.range}
                  className="grid grid-cols-2 px-6 py-4 border-b border-black/5 last:border-0 hover:bg-white transition-colors"
                >
                  <span className="text-sm font-semibold text-black">
                    {tier.range} credits
                  </span>
                  <span className="text-sm font-bold text-right" style={{ color: i === CREDIT_TIERS.length - 1 ? "#52dcb3" : "#5a20ff" }}>
                    {tier.price}
                    {i === CREDIT_TIERS.length - 1 && (
                      <span className="ml-2 text-[10px] font-bold uppercase bg-[#52dcb3]/10 text-[#52dcb3] px-2 py-0.5 rounded-full">
                        Best Value
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What is a Credit? */}
      <section className="relative z-10 bg-gradient-to-br from-[#5a20ff]/[0.03] to-[#52dcb3]/[0.03]">
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold text-black mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              What is a Credit?
            </h2>
            <p className="text-black/50">
              One credit, three ways to use it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
            {[
              {
                icon: Search,
                title: "1 Contact Reveal",
                desc: "Verified email + phone number for any contact in our database.",
                color: "#5a20ff",
              },
              {
                icon: MessageSquare,
                title: "1 AI Outreach Message",
                desc: "Personalised message crafted by AI using the contact's profile.",
                color: "#52dcb3",
              },
              {
                icon: Database,
                title: "1 Bulk Enrichment Profile",
                desc: "Enrich a contact from your own database via EQenrich.",
                color: "#3b82f6",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white rounded-2xl p-6 border border-black/5 text-center hover:shadow-lg transition-shadow"
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${item.color}10` }}
                  >
                    <Icon className="w-7 h-7" style={{ color: item.color }} />
                  </div>
                  <h3 className="text-base font-bold text-black mb-2">{item.title}</h3>
                  <p className="text-sm text-black/50 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>

          {/* FAQ */}
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <HelpCircle className="w-5 h-5 text-[#5a20ff]" />
              <h3
                className="text-xl font-bold text-black"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Frequently Asked Questions
              </h3>
            </div>
            <div className="bg-white rounded-2xl border border-black/5 px-6">
              {FAQ_ITEMS.map((item) => (
                <FAQItem key={item.q} item={item} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="relative z-10 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Ready to find your next client?
          </h2>
          <p className="text-white/70 mb-8 text-lg">
            Search for free. Pay only when you're ready to connect.
          </p>
          <button
            onClick={() => navigate("/app")}
            className="px-8 py-4 bg-white text-[#5a20ff] font-bold rounded-full text-base hover:shadow-2xl transition-all active:scale-95 shadow-xl inline-flex items-center gap-2"
          >
            Start Searching for Free
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-black/[0.02] border-t border-black/5 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-lg flex items-center justify-center">
              <Search className="w-3 h-3 text-white" />
            </div>
            <span className="text-sm font-semibold text-black/40">
              Scout by EQ
            </span>
          </div>
          <div className="flex items-center gap-6 text-xs text-black/30">
            <a href="/legal/privacy" className="hover:text-black/60 transition-colors">Privacy</a>
            <a href="/legal/terms" className="hover:text-black/60 transition-colors">Terms</a>
            <a href="mailto:hello@eq.app" className="hover:text-black/60 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
